import { useMemo } from 'react';
import { endOfMonth, parseDate, startOfMonth } from '@internationalized/date';
import { createFileRoute } from '@tanstack/react-router';
import { Big } from 'big.js';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { useIxfAccountingV1AssetServiceDeltas } from '@interxfi/api-client/generated/hooks/ixf-accounting-v1-asset-service-deltas';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { CurrencyFormat } from '@interxfi/design-system/components/format/currency';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import type { MonoTypographyProps } from '@interxfi/design-system/components/typography';
import { Typography } from '@interxfi/design-system/components/typography';
import { useCurrentDate } from '@interxfi/design-system/util/date';
import type { FilterState } from '@interxfi/table-config/filter';
import { IxfAccountingV1TransactionQueryServiceTransactionListFilterableFields } from '@interxfi/types';
import { PageTabRail } from '../../../../../../../../../../../../components/page-tab-rail/page-tab-rail';
import {
  type PortalTabbedNavigationTab,
  TabbedNavigationGroup,
} from '../../../../../../../../../../../../components/router/router-tabbed-navigation';
import { AccountingCoaAccountBreadcrumb } from '../../../../../../../../../../../../features/accounting/chart-of-accounts/accounting-coa-account-breadcrumb';
import {
  AccountingTransactionsTable,
  type AccountingTransactionTableHeaderProps,
} from '../../../../../../../../../../../../features/accounting/transaction/accounting-transactions-table';

const searchSchema = z.object({
  fromDate: z.iso.date().optional(),
  toDate: z.iso.date().optional(),
});

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/finance/_finance/accounting/account/_account/$accountId/_layout/',
)({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ClearingFinanceAccountingCoa,
      label: 'Transactions',
      order: 0,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ClearingFinanceAccountingCoa>,
  validateSearch: searchSchema,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `coa-account-${params.accountId}`,
        label: <AccountingCoaAccountBreadcrumb accountId={params.accountId} />,
        to: `/clearing/finance/accounting/account/${params.accountId}`,
        suffix: <CopyButton content={params.accountId} label="Copy Account ID" tooltip={{ copy: params.accountId, copied: 'Copied Account ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  component: ChartOfAccountsAccountDetail,
});

const currencyTypography = {
  variant: 'mono',
  lineHeight: '1.5rem',
  size: '1.5rem',
  tone: 'md',
  inheritance: 'current',
} as const satisfies MonoTypographyProps;

const currencyFloatingTypography = {
  variant: 'mono',
  lineHeight: '1.125rem',
  size: '1.125rem',
  tone: 'md',
  inheritance: 'current',
} as const satisfies MonoTypographyProps;

function TableHeader({ filter, filterProps }: AccountingTransactionTableHeaderProps) {
  const { accountId } = Route.useParams();
  const dateFilter = filterProps?.values?.[IxfAccountingV1TransactionQueryServiceTransactionListFilterableFields.DataDate];
  const fromDate = dateFilter && 'date' in dateFilter ? dateFilter.date.start : undefined;
  const toDate = dateFilter && 'date' in dateFilter ? dateFilter.date.end : undefined;
  const { data } = useIxfAccountingV1AssetServiceDeltas(fromDate && toDate ? { accountId, fromDate, toDate } : undefined);
  const totals = data?.pages?.map((page) => page?.totals)?.find(Boolean);
  const balances = match(totals)
    .with({ temporary: P.not(P.nullish) }, (t) => [{ value: t.temporary.total, label: `Total (${fromDate} - ${toDate})` }] as const)
    .with({ permanent: P.not(P.nullish) }, (p) => {
      let change = '0.00';
      try {
        change = new Big(p.permanent.closingBalance).sub(p.permanent.openingBalance).toFixed(2).toString();
      } catch {}

      return [
        { value: p.permanent.openingBalance, label: `Opening Balance (${fromDate})` },
        { value: p.permanent.closingBalance, label: `Closing Balance (${toDate})` },
        { value: change, label: 'Change' },
      ] as const;
    })
    .otherwise(() => []);

  return (
    <VerticalStack gap={7}>
      <PageTabRail actions={filter} group={TabbedNavigationGroup.ClearingFinanceAccountingCoa} />

      {fromDate && toDate ? (
        <HorizontalStack blockAlign="center" gap={7}>
          {balances.length ? (
            balances.map((balance) => (
              <VerticalStack gap={2} key={balance.label}>
                <CurrencyFormat
                  currency="USD"
                  floatingFractionDigits
                  floatingOffset={4}
                  floatingTypography={currencyFloatingTypography}
                  signDisplay="negative"
                  typography={currencyTypography}
                  value={balance.value || '0.00'}
                />

                <Typography lineHeight="1rem" size="0.75rem">
                  {balance.label}
                </Typography>
              </VerticalStack>
            ))
          ) : (
            <VerticalStack gap={2}>
              <Skeleton style={{ width: 120, height: 24 }} type="rect" />
              <Skeleton style={{ width: 176, height: 16 }} type="rect" />
            </VerticalStack>
          )}
        </HorizontalStack>
      ) : null}
    </VerticalStack>
  );
}

function ChartOfAccountsAccountDetail() {
  const { accountId } = Route.useParams();
  const { fromDate, toDate } = Route.useSearch();
  const { date: currentDate } = useCurrentDate();
  const initialFilters: FilterState<IxfAccountingV1TransactionQueryServiceTransactionListFilterableFields> = useMemo(() => {
    let start = startOfMonth(currentDate);
    let end = endOfMonth(currentDate);

    if (fromDate) {
      try {
        start = parseDate(fromDate);
      } catch {}
    }

    if (toDate) {
      try {
        end = parseDate(toDate);
      } catch {}
    }

    return [
      {
        type: {
          filters: [
            {
              id: IxfAccountingV1TransactionQueryServiceTransactionListFilterableFields.DataDate,
              value: { range: { min: start.toString(), max: end.toString() } },
            },
          ],
        },
      },
    ] as const satisfies FilterState<IxfAccountingV1TransactionQueryServiceTransactionListFilterableFields>;
  }, [currentDate, fromDate, toDate]);

  return (
    <PageLayout.Section>
      <AccountingTransactionsTable accountId={accountId} HeaderActions={TableHeader} initialFilters={initialFilters} />
    </PageLayout.Section>
  );
}
